import { useQuery } from '@tanstack/react-query';
import { logger } from '@/lib/logger';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryKeys';
import { svgToThumbnail } from '@/utils/svgToThumbnail';
import { getFloorPlanUrl } from '@/services/floorPlanService';

export function useFloorPlanThumbnail(floorPlan: {
  id: string;
  file_path: string;
  file_type: string;
  is_cad_file?: boolean;
  updated_at?: string;
}) {
  // Debug logging
  logger.log('useFloorPlanThumbnail:', {
    id: floorPlan.id,
    file_type: floorPlan.file_type,
    is_cad_file: floorPlan.is_cad_file,
    file_path: floorPlan.file_path,
  });

  const filePathLower = floorPlan.file_path?.toLowerCase?.() ?? '';
  const isSvgFile = filePathLower.endsWith('.svg');
  const shouldTreatAsCad =
    (floorPlan.is_cad_file || floorPlan.file_type === 'image/svg+xml') && isSvgFile;

  // For CAD files, fetch the PNG URL from metadata
  const { data: dwgMetadata } = useQuery({
    queryKey: [...queryKeys.floorPlan(floorPlan.id), 'dwg-metadata-thumbnail'],
    queryFn: async () => {
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('floor_plan_dwg_metadata')
        .select('svg_url, png_url')
        .eq('floor_plan_id', floorPlan.id)
        .single();

      if (error?.code && ['PGRST116', '406'].includes(error.code)) {
        return null;
      }

      if (error && !error.code) {
        logger.error('Error fetching DWG metadata for thumbnail:', error);
      }

      return data;
    },
    enabled: shouldTreatAsCad,
  });

  // Generate high-quality thumbnail for CAD files
  const { data: thumbnailData, isLoading: isThumbnailGenerating } = useQuery({
    queryKey: [...queryKeys.floorPlan(floorPlan.id), 'cad-thumbnail-hq'],
    queryFn: async () => {
      if (!dwgMetadata?.svg_url) return null;

      try {
        // Generate a high-quality PNG thumbnail from the SVG
        const thumbnailDataUrl = await svgToThumbnail(dwgMetadata.svg_url, 400, 300);
        return thumbnailDataUrl;
      } catch (error) {
        logger.error('Failed to generate thumbnail:', error);
        // Fall back to PNG URL if thumbnail generation fails
        return dwgMetadata.png_url;
      }
    },
    enabled: !!dwgMetadata?.svg_url,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
  });

  // For CAD files, use generated thumbnail or fallback to PNG
  if (shouldTreatAsCad && dwgMetadata) {
    logger.log('CAD file detected, thumbnail status:', {
      hasDwgMetadata: !!dwgMetadata,
      hasThumbnail: !!thumbnailData,
      isGenerating: isThumbnailGenerating,
    });

    const fallbackPng =
      typeof dwgMetadata.png_url === 'string' && dwgMetadata.png_url.trim().length > 0
        ? dwgMetadata.png_url
        : null;

    const cadThumbnail =
      thumbnailData ?? fallbackPng ?? getFloorPlanUrl(floorPlan.file_path, floorPlan.updated_at);

    return {
      thumbnailUrl: cadThumbnail || null,
      isLoading: dwgMetadata === undefined || isThumbnailGenerating,
      isCadFile: true,
      isSvg: false, // We're returning a PNG thumbnail now
    };
  }

  // For regular files, generate the public URL with cache busting
  const urlWithCacheBuster = getFloorPlanUrl(floorPlan.file_path, floorPlan.updated_at);

  return {
    thumbnailUrl: urlWithCacheBuster,
    isLoading: false,
    isCadFile: false,
    isSvg: false,
  };
}
